/**
 * Product Fitment Script (Apex Theme Version - Hide by Default)
 */
document.addEventListener('DOMContentLoaded', () => {
  const fitmentSections = document.querySelectorAll('.product-fitment-section');

  fitmentSections.forEach(section => {
    const sectionId = section.dataset.sectionId;
    const mode = section.dataset.mode || 'api';
    const endpoint = section.dataset.apiEndpoint;
    const partNumber = section.dataset.partNumber;
    const hideIfEmpty = section.dataset.hideIfEmpty === 'true';

    // If there is no part number, there's nothing to search. Leave hidden.
    // (Metafield mode carries its data in the page and needs no part number.)
    if (mode !== 'metafield' && !partNumber) return;

    // DOM Elements
    const loadingElement = section.querySelector('.product-fitment__loading');
    const resultsContainer = section.querySelector('.product-fitment__results');
    const tableHeader = section.querySelector('.product-fitment__table-header');
    const errorMessageElement = section.querySelector('.product-fitment__error-message');
    const filterInput = section.querySelector(`#fitment-filter-${sectionId}`);
    const badgeElement = section.querySelector('.product-fitment__badge');

    let allFitments = [];

    // Function to unhide the section if we find data
    const showSection = () => {
      section.style.display = 'block';
    };

    const displayError = (message) => {
      if (hideIfEmpty) return; // If we're hiding empty/errors, just do nothing and leave it invisible
      
      showSection();
      if (loadingElement) loadingElement.style.display = 'none';
      if (tableHeader) tableHeader.style.display = 'none';
      if (errorMessageElement) {
        errorMessageElement.innerHTML = `
          <div class="fitment-error-card">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: #df472e; flex-shrink:0;">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span>${escapeHtml(message)}</span>
          </div>
        `;
        errorMessageElement.style.display = 'block';
      }
      console.warn(`Product Fitment: ${message}`);
    };

    const escapeHtml = (unsafe) => {
        if (!unsafe) return '';
        return unsafe.toString()
             .replace(/&/g, "&amp;")
             .replace(/</g, "&lt;")
             .replace(/>/g, "&gt;")
             .replace(/"/g, "&quot;")
             .replace(/'/g, "&#039;");
     };

    const displayResults = (fitmentsToDisplay) => {
      // Show Header Row
      if (tableHeader) tableHeader.style.display = 'grid';

      // Build List
      const listHtml = fitmentsToDisplay.map(fitment => `
        <li class="product-fitment__list-item">
          <span class="product-fitment__type">${escapeHtml(fitment.productType || '—')}</span>
          <span class="product-fitment__make">${escapeHtml(fitment.fitmentMake || '')}</span>
          <span class="product-fitment__model">${escapeHtml(fitment.fitmentModel || '')}</span>
          <span class="product-fitment__years">${escapeHtml(fitment.fitmentYears || '')}</span>
        </li>
      `).join('');

      resultsContainer.innerHTML = `<ul class="product-fitment__list" id="product-fitment-results-list-${sectionId}">${listHtml}</ul>`;

      resultsContainer.classList.remove('fitment-fade-in');
      void resultsContainer.offsetWidth; // Trigger reflow
      resultsContainer.classList.add('fitment-fade-in');
    };

    const handleFilter = (event) => {
        const searchTerm = event.target.value.trim().toLocaleLowerCase();
        if (!allFitments) return;

        const filteredFitments = allFitments.filter(fitment => {
            const type = (fitment.productType || '').toLocaleLowerCase();
            const make = (fitment.fitmentMake || '').toLocaleLowerCase();
            const model = (fitment.fitmentModel || '').toLocaleLowerCase();
            const years = (fitment.fitmentYears || '').toLocaleLowerCase();
            // Type is searchable too, so "snowmobile" narrows the list the way a shopper expects.
            return type.includes(searchTerm) || make.includes(searchTerm)
                || model.includes(searchTerm) || years.includes(searchTerm);
        });

        // Update count badge
        if (badgeElement) {
          if (searchTerm) {
            badgeElement.textContent = `${filteredFitments.length} Matches`;
          } else {
            badgeElement.textContent = `${allFitments.length} Vehicles`;
          }
        }

        // Handle No Filter Match inside the already visible section
        if (filteredFitments.length === 0) {
            resultsContainer.innerHTML = `
              <div class="fitment-no-match-card">
                <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" style="color: color-mix(in srgb, rgba(var(--color-foreground)) 40%, transparent); margin-bottom: 8px;">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  <line x1="8" y1="11" x2="14" y2="11"></line>
                </svg>
                <p class="product-fitment__results--no-match">No vehicles match your search query.</p>
              </div>
            `;
            if (tableHeader) tableHeader.style.display = 'none';
        } else {
            displayResults(filteredFitments);
        }
    };

    if (filterInput) filterInput.addEventListener('input', handleFilter);

    const fetchFitmentData = async () => {
      const url = `${endpoint}?itemNumber=${encodeURIComponent(partNumber)}`;

      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'application/json'
          }
        });

        // If the item doesn't exist in the API (Non-Gamma product), it usually returns a 400 Bad Request
        if (!response.ok) {
           if (hideIfEmpty) return; // Leave it permanently invisible
           throw new Error(`Item not found in database.`);
        }

        const result = await response.json();

        if (result.status === 'success') {
          allFitments = result.data?.fitments || [];
          
          if (allFitments.length === 0) {
             if (hideIfEmpty) return; // Leave it permanently invisible
             
             showSection();
             if (loadingElement) loadingElement.style.display = 'none';
             if (filterInput) filterInput.disabled = true;
             if (tableHeader) tableHeader.style.display = 'none';
             resultsContainer.innerHTML = `
               <div class="fitment-empty-card">
                 <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" style="color: color-mix(in srgb, rgba(var(--color-foreground)) 35%, transparent);">
                   <circle cx="12" cy="12" r="10"></circle>
                   <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
                   <line x1="9" y1="9" x2="15" y2="15" stroke="rgba(var(--color-accent-1))" stroke-width="1.5"></line>
                 </svg>
                 <div class="fitment-empty-title">No Compatibility Data</div>
                 <div class="fitment-empty-text">No specific vehicle fitment records were found for this part number.</div>
               </div>
             `;
             return;
          }
          
          // We have data! Sort it and show the section.
          allFitments.sort((a, b) => {
            const makeA = (a.fitmentMake || '').toLowerCase();
            const makeB = (b.fitmentMake || '').toLowerCase();
            if (makeA < makeB) return -1;
            if (makeA > makeB) return 1;
            // If Makes match, sort by Year (Newest to Oldest)
            if (b.fitmentYears > a.fitmentYears) return 1;
            if (b.fitmentYears < a.fitmentYears) return -1;
            
            return 0;
          });

          // Unhide the main container now that we are ready
          showSection();
          if (loadingElement) loadingElement.style.display = 'none';
          
          if (badgeElement) {
            badgeElement.textContent = `${allFitments.length} Vehicles`;
            badgeElement.style.display = 'inline-flex';
          }

          displayResults(allFitments);
          if (filterInput) filterInput.disabled = false;

        } else {
          displayError(result.error?.message || 'API Error');
        }

      } catch (error) {
        displayError('Unable to load fitment data.');
      }
    };

    // Metafield mode: the table was rendered into the page server-side from the
    // fitment.machines variant metafield. Load the embedded copy of the data so the
    // search box works, and skip the API entirely.
    if (mode === 'metafield') {
      /**
       * The embedded copy covers every option, not just the one the page loaded with. The
       * fitment is filed per variant, so a part sold as a left and a right can list different
       * machines on each - and the table used to keep showing the option the page opened on,
       * however many times the shopper changed it. Redrawing from data already here costs no
       * request, so the table changes with the option as fast as the price does.
       */
      let variantFitments = [];
      const island = section.querySelector('[data-fitment-variants]');
      try {
        variantFitments = JSON.parse(island ? island.textContent : '[]') || [];
      } catch (e) {
        console.warn('Product Fitment: could not parse embedded fitment data.');
        variantFitments = [];
      }

      // Reshaped to the API's field names, so displayResults and the search box below work
      // unchanged in both modes.
      const rowsFor = (variantId) => {
        const match = variantFitments.filter((v) => String(v.id) === String(variantId))[0];
        return (match ? match.machines : []).map((machine) => ({
          productType: machine.type,
          fitmentMake: machine.make,
          fitmentModel: machine.model,
          fitmentYears: machine.years
        }));
      };

      // Said in the same words as the server-rendered version of this card in
      // sections/product-fitment.liquid, so switching option never changes the wording.
      const drawEmpty = () => {
        if (tableHeader) tableHeader.style.display = 'none';
        resultsContainer.innerHTML = `
          <div class="fitment-empty-card">
            <div class="fitment-empty-title">No vehicle list for this option</div>
            <div class="fitment-empty-text">Another option of this product has one. Change the option above to see it.</div>
          </div>
        `;
      };

      const showVariant = (variantId, redraw) => {
        allFitments = rowsFor(variantId);
        if (filterInput) {
          filterInput.disabled = allFitments.length === 0;
          if (redraw) filterInput.value = '';
        }
        if (badgeElement) badgeElement.textContent = `${allFitments.length} Vehicles`;
        // On first load the server already drew this option; only a change needs redrawing.
        if (!redraw) return;
        if (allFitments.length === 0) drawEmpty();
        else displayResults(allFitments);
      };

      showVariant(section.dataset.selectedVariant, false);

      // The theme announces the change as product:variant-change; the hidden id field on the
      // add-to-cart form changes with it, and catches anything that sets the variant quietly.
      document.addEventListener('product:variant-change', (event) => {
        const variant = event.detail && event.detail.variant;
        if (variant) showVariant(variant.id, true);
      });
      document.addEventListener('change', (event) => {
        const input = event.target;
        if (!input || input.name !== 'id' || !input.value) return;
        showVariant(input.value, true);
      });
      return;
    }

    // Kick off the silent check
    fetchFitmentData();
  });
});